import React, { useEffect, useState, useCallback } from 'react';
import { 
  Modal, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Button, 
  Space,
  Tag,
  Typography,
  Divider
} from 'antd';
import { 
  PlusOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  FolderOutlined,
  UserOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import todoCategoryService from '../../../services/todoCategoryService';
import TodoCategoryManage from './TodoCategoryManage';
import { TodoLabelSelect, TodoMemberSelect } from './TodoPickers';
import TodoLabelTag from './TodoLabelTag';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const TodoForm = ({ 
  visible, 
  onCancel, 
  onSubmit, 
  title = "Add Todo", 
  initialValues = null,
  loading = false,
  categories = [],
  onCategoriesRefresh,
  /*
  | From /todos/options. `relatedTypes` is already filtered by the organization's
  | LICENSE server-side, so this form never offers a link type the org cannot use —
  | there is no plan logic on the client at all.
  */
  assignees = [],
  relatedTypes = [],
}) => {
  const [form] = Form.useForm();
  const [localCategories, setLocalCategories] = useState([]);
  const [manageCategoriesVisible, setManageCategoriesVisible] = useState(false);

  const effectiveCategories = categories?.length ? categories : localCategories;

  const loadCategories = useCallback(async () => {
    try {
      const response = await todoCategoryService.getCategories();
      const result = response?.data || response;
      const list = (result?.data && Array.isArray(result.data)) ? result.data : (Array.isArray(result) ? result : []);
      setLocalCategories(list);
    } catch (e) {
      console.error('Failed to load categories', e);
    }
  }, []);

  useEffect(() => {
    if (visible && effectiveCategories.length === 0) {
      loadCategories();
    }
  }, [visible, effectiveCategories.length, loadCategories]);

  useEffect(() => {
    if (visible) {
      if (initialValues) {
        // Convert dates to dayjs objects for the form; members and labels are lists of public_ids
        const rawDue = initialValues.due_date ?? initialValues.dueDate;
        const formValues = {
          ...initialValues,
          dueDate: rawDue ? dayjs(rawDue) : null,
          member_ids: (initialValues.members || []).map((m) => m.id),
          label_ids: (initialValues.labels || []).map((l) => l.id)
        };
        form.setFieldsValue(formValues);
      } else {
        form.resetFields();
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Convert dayjs to ISO string for API
      const todoData = {
        ...values,
        due_date: values.dueDate ? values.dueDate.format('YYYY-MM-DD') : null,

        completed: initialValues?.completed || false,
        starred: initialValues?.starred || false,
        /*
        | Lists, not single values.
        |
        | `assigned_to` and `category_id` were dropped when members and labels became
        | many-to-many — the API ignored them, so picking an assignee or a category in
        | this form silently saved nothing. These are the fields the API actually reads.
        */
        member_ids: values.member_ids ?? [],
        label_ids: values.label_ids ?? [],
        // Only sent as a pair — a type with no record, or the reverse, is not a link.
        related_type: values.related_type || null,
        related_id: values.related_id || null,
      };

      await onSubmit(todoData);
      form.resetFields();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Add form field change listener for preview
  const [previewData, setPreviewData] = useState({});

  const handleFormChange = (changedFields, allFields) => {
    setPreviewData(allFields);
  };

  const priorityOptions = [
    {
      value: 'high',
      label: 'High',
      icon: <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />,
      color: 'red'
    },
    {
      value: 'medium',
      label: 'Medium',
      icon: <ClockCircleOutlined style={{ color: '#faad14' }} />,
      color: 'orange'
    },
    {
      value: 'low',
      label: 'Low',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      color: 'green'
    }
  ];

  const categoryOptions = effectiveCategories.map((c) => ({
    value: c.id,
    label: c.name,
    color: c.color || 'default'
  }));

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          onClick={handleSubmit}
          loading={loading}
        >
          {initialValues ? 'Update' : 'Create'}
        </Button>
      ]}
      width={600}
      destroyOnHidden
    >
      <Form
        form={form}
        size="large"
        layout="vertical"
        initialValues={{
          priority: 'medium',
          member_ids: [],
          label_ids: [],
        }}
        onFieldsChange={handleFormChange}
      >
        {/* Title */}
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please enter a title' },
            { max: 100, message: 'Title must be less than 100 characters' }
          ]}
        >
          <Input 
            placeholder="Enter todo title"
            maxLength={100}
            showCount
          />
        </Form.Item>




        

{/* Priority and Category */}
<div style={{ display: 'flex', gap: 16 }}>
  <Form.Item
    name="priority"
    label="Priority"
    style={{ flex: 1 }}
  >
    <Select placeholder="Select priority">
      {priorityOptions.map(option => (
        <Option key={option.value} value={option.value}>
          <Space>
            {option.icon}
            {option.label}
          </Space>
        </Option>
      ))}
    </Select>
  </Form.Item>

  <Form.Item
    name="label_ids"
    label={
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <span>Labels</span>
        <Button
          type="link"
          onClick={() => setManageCategoriesVisible(true)}
          size="small"
          style={{ padding: 0 }}
        >
          Manage labels
        </Button>
      </Space>
    }
    style={{ flex: 1 }}
  >
    <TodoLabelSelect labels={effectiveCategories} />
  </Form.Item>
</div>

<TodoCategoryManage
  visible={manageCategoriesVisible}
  onClose={() => setManageCategoriesVisible(false)}
  categories={effectiveCategories}
  onRefresh={async () => {
    await loadCategories();
    onCategoriesRefresh?.();
  }}
/>

{/* Assignment — the field that turns a private list into a team one. */}
<Form.Item
  name="member_ids"
  label={
    <Space>
      <UserOutlined />
      Members
    </Space>
  }
>
  <TodoMemberSelect assignees={assignees} />
</Form.Item>

{/*
  Link to a record. The type list arrives already filtered by licence, so an
  organization without the purchases feature simply never sees "Bill" or "Supplier".
  Hidden entirely when nothing is linkable.
*/}
{relatedTypes.length > 0 && (
  <div style={{ display: 'flex', gap: 8 }}>
    <Form.Item name="related_type" label="Relates to" style={{ flex: '0 0 40%' }}>
      <Select placeholder="Nothing" allowClear>
        {relatedTypes.map((t) => (
          <Option key={t.type} value={t.type}>{t.label}</Option>
        ))}
      </Select>
    </Form.Item>

    <Form.Item
      name="related_id"
      label="Record"
      style={{ flex: 1 }}
      tooltip="Paste or type the record's reference"
    >
      <Input placeholder="e.g. INV-2041" />
    </Form.Item>
  </div>
)}

{/* Due Date */}
<Form.Item
  name="dueDate"
  label={
    <Space>
      <CalendarOutlined />
      Due Date
    </Space>
  }
>
  <DatePicker
    placeholder="Select due date"
    style={{ width: '100%' }}
    showTime={false}
    format="YYYY-MM-DD"
    disabledDate={(current) => {
      // Disable past dates
      return current && current < dayjs().startOf('day');
    }}
  />
</Form.Item>


        {/* Description */}
        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 500, message: 'Description must be less than 500 characters' }
          ]}
        >
          <TextArea
            placeholder="Enter todo description (optional)"
            rows={3}
            maxLength={500}
            showCount
          />
        </Form.Item>

 

        {/* Preview */}
        {/* <Form.Item label="Preview">
          <div style={{ 

          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              This is how your todo will appear in the list
            </Text>
            <div style={{ marginTop: 8 }}>
              {previewData.title && (
                <div style={{ fontWeight: 500 }}>
                  {previewData.title}
                </div>
              )}
              {previewData.description && (
                <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                  {previewData.description}
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                {previewData.priority && (
                  <Tag 
                    color={priorityOptions.find(p => p.value === previewData.priority)?.color}
                    size="small"
                    style={{ marginRight: 8 }}
                  >
                    {previewData.priority}
                  </Tag>
                )}
                {(previewData.label_ids || []).map((id) => {
                  const label = categoryOptions.find((c) => c.value === id);
                  return label ? (
                    <TodoLabelTag key={id} color={label.color} style={{ marginRight: 8 }}>
                      {label.label}
                    </TodoLabelTag>
                  ) : null;
                })}
                {previewData.tags?.map(tag => (
                  <Tag key={tag} size="small" style={{ marginRight: 4 }}>
                    {tag}
                  </Tag>
                ))}
              </div>
            </div>
          </div>
        </Form.Item> */}
      </Form>
    </Modal>
  );
};

export default TodoForm; 